import cx from 'classnames';
import { parseISO } from 'date-fns';
import { Button, ButtonVariants } from '@components/button/Button';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import {
  parseToShortDateRange,
  toPrettyDateTime,
} from '@shared/utils/dateFormatters';
import typography from '~styles/typography.scss';
import { HotelLinkModal } from './HotelLinkModal';
import {
  useLinkOperaReservation,
  useOperaLink,
  useOperaLinkCandidates,
  useRefreshOperaLink,
  useRejectOperaMatch,
} from './hooks';
import styles from './HotelLinkSection.scss';
import type { OperaHotelNote, OperaLinkDTO } from './types';

// "HOUSEKEEPING" -> "Housekeeping". The desk configures department ids upper-case
// (OPERA_HOTEL_NOTE_TRACE_DEPARTMENTS); this is display formatting only, never a lookup key.
const formatDepartmentLabel = (department: string): string =>
  department.charAt(0) + department.slice(1).toLowerCase();

export interface HotelLinkSectionProps {
  reservationId: string;
  className?: string;
  onUpdate?: () => void;
}

const STATUS_LABEL: Record<OperaLinkDTO['status'], string> = {
  suggested: 'Suggested match',
  confirmed: 'Confirmed stay',
  auto_linked: 'Auto linked',
  rejected: 'Rejected',
  lapsed: 'Lapsed stay',
};

const STATUS_STYLE: Record<OperaLinkDTO['status'], string> = {
  suggested: styles.statusPillSuggested,
  confirmed: styles.statusPillConfirmed,
  auto_linked: styles.statusPillConfirmed,
  rejected: styles.statusPillLapsed,
  lapsed: styles.statusPillLapsed,
};

const HotelLinkStatusPill = ({
  status,
}: {
  status: OperaLinkDTO['status'];
}) => (
  <span className={cx(typography.t1, styles.statusPill, STATUS_STYLE[status])}>
    {STATUS_LABEL[status]}
  </span>
);

// PRD section 4: a hotel-sourced value displays beside Peak's own, with a source label, never
// overwriting it. One component for phone, email, accompanying guests, allergies, and occasion,
// which otherwise differed only in label and value.
const SourcedField = ({
  label,
  value,
  source,
}: {
  label: string;
  value: string;
  source: string;
}) => (
  <span className={typography.t2}>
    {label}: {value}{' '}
    <span className={cx(typography.t1, styles.sourceLabel)}>{source}</span>
  </span>
);

export const HotelLinkSection = ({
  reservationId,
  className,
  onUpdate,
}: HotelLinkSectionProps) => {
  const { data, isLoading, isError } = useOperaLink(reservationId);
  const {
    isOpen: isModalOpen,
    open: openModal,
    close: closeModal,
  } = useIsOpen();
  const linkMutation = useLinkOperaReservation(reservationId, onUpdate);
  const rejectMutation = useRejectOperaMatch(reservationId, onUpdate);
  const refreshMutation = useRefreshOperaLink(reservationId, onUpdate);

  // Only worth searching for a fresh candidate list while there is still something to decide:
  // no link yet, a suggested one still waiting on the candidate details it displays (guest
  // name, dates, room, VIP), or the "find a different stay" modal open. A confirmed,
  // auto-linked, or lapsed link never reads `candidates` at all (see below), so there is
  // nothing more to find for those until the reservation is unlinked or relinked.
  const { data: candidatesData } = useOperaLinkCandidates(
    reservationId,
    !data?.link || data.link.status === 'suggested' || isModalOpen,
  );

  const isMutating =
    linkMutation.isPending ||
    rejectMutation.isPending ||
    refreshMutation.isPending;

  const renderBody = () => {
    if (isLoading) {
      return <p className={typography.t2}>Loading...</p>;
    }

    if (isError || !data) {
      return (
        <p className={typography.t2}>
          Unable to load hotel reservation status.
        </p>
      );
    }

    const { link, hotelUnavailable } = data;
    const candidates = candidatesData?.candidates ?? [];

    // A live OHIP outage degrades to hotelUnavailable: true with an empty candidate list
    // (OperaLinkController.getCandidates), which otherwise looks identical to a genuine
    // no-match. This only renders once a link already exists -- the stored link itself came
    // from Peak's own database and is unaffected, so it is still the "last known" state worth
    // naming, unlike the no-link case below where there is nothing yet to call "last known."
    const hotelUnavailableNotice = hotelUnavailable && link && (
      <p className={cx(typography.t1, styles.hotelUnavailableNotice)}>
        Hotel unreachable, showing the last known link
      </p>
    );

    if (!link || link.status === 'rejected') {
      return (
        <Button
          variant={ButtonVariants.Secondary}
          label="Link to hotel reservation"
          onClick={openModal}
        />
      );
    }

    if (link.status === 'suggested') {
      const suggestedCandidate = candidates.find(
        (candidate) => candidate.operaReservationId === link.operaReservationId,
      );

      return (
        <div className={styles.suggestionCard} aria-label="suggested match">
          {hotelUnavailableNotice}
          <HotelLinkStatusPill status={link.status} />
          {suggestedCandidate && (
            <div className={styles.suggestionDetails}>
              <p className={typography.h7}>{suggestedCandidate.guestName}</p>
              <p className={cx(typography.t2, styles.detail)}>
                {parseToShortDateRange(
                  suggestedCandidate.arrivalDate,
                  suggestedCandidate.departureDate,
                )}
              </p>
              {suggestedCandidate.roomNumber && (
                <p className={cx(typography.t2, styles.detail)}>
                  Room {suggestedCandidate.roomNumber}
                </p>
              )}
              {suggestedCandidate.vipCode && (
                <p className={cx(typography.t2, styles.detail)}>
                  VIP: {suggestedCandidate.vipCode}
                </p>
              )}
            </div>
          )}
          <div className={styles.actions}>
            <Button
              variant={ButtonVariants.Primary}
              label="Confirm"
              isDisabled={isMutating}
              onClick={() => linkMutation.mutate(link.operaReservationId)}
            />
            <Button
              variant={ButtonVariants.Tertiary}
              label="Reject"
              isDisabled={isMutating}
              onClick={() => rejectMutation.mutate(link.operaReservationId)}
            />
          </div>
        </div>
      );
    }

    // confirmed | auto_linked | lapsed
    return (
      <div className={styles.summary}>
        {hotelUnavailableNotice}
        <HotelLinkStatusPill status={link.status} />
        <div className={styles.summaryDetails}>
          {link.roomNumber && (
            <span className={typography.t2}>Room {link.roomNumber}</span>
          )}
          {link.vipCode && (
            <span className={typography.t2}>
              VIP: {link.vipCode}
              {link.vipDescription ? ` ${link.vipDescription}` : ''}
            </span>
          )}
          {/* PRD section 4: hotel-sourced values display beside Peak's own with a source
              label, never overwriting them -- the guest's Peak phone and email are shown
              elsewhere in this drawer. */}
          {link.operaPhone && (
            <SourcedField
              label="Phone"
              value={link.operaPhone}
              source="Opera"
            />
          )}
          {link.operaEmail && (
            <SourcedField
              label="Email"
              value={link.operaEmail}
              source="Opera"
            />
          )}
          {link.accompanyingGuests && (
            <SourcedField
              label="Accompanying"
              value={link.accompanyingGuests}
              source="Opera"
            />
          )}
          {/* Opera's copy only, shown beside Peak's own allergy tags (GuestTags, rendered
              separately in the drawer) rather than merged into them -- the union is visual. */}
          {link.operaAllergies && link.operaAllergies.length > 0 && (
            <SourcedField
              label="Allergies"
              value={link.operaAllergies.map((entry) => entry.value).join(', ')}
              source="Opera"
            />
          )}
          {link.operaOccasion && (
            <SourcedField
              label="Occasion"
              value={link.operaOccasion}
              source="Opera"
            />
          )}
          {/* Display-only, never merged into Peak's own guest note (OccupantDetails.tsx) --
              same union-never-a-merge rule as allergies and occasion above. */}
          {link.hotelNotes?.map((note: OperaHotelNote, index: number) => (
            <SourcedField
              // Traces carry no stable client-side key; department+index is stable for the
              // life of one render of an immutable API response.
              key={`${note.department}-${index}`}
              label={formatDepartmentLabel(note.department)}
              value={note.text}
              source="Opera"
            />
          ))}
          <div className={styles.lastSyncedRow}>
            <span className={cx(typography.t2, styles.lastSynced)}>
              Last synced:{' '}
              {link.lastSyncedAt
                ? toPrettyDateTime(parseISO(link.lastSyncedAt))
                : 'Never'}
            </span>
            {/* Only a live match can be refreshed -- the endpoint 404s on a lapsed or
                suggested link, so no button is offered for one. */}
            {(link.status === 'confirmed' || link.status === 'auto_linked') && (
              <Button
                variant={ButtonVariants.Tertiary}
                label="Refresh"
                isLoading={refreshMutation.isPending}
                isDisabled={isMutating}
                onClick={() => refreshMutation.mutate()}
              />
            )}
          </div>
        </div>
        <button
          type="button"
          className={cx(typography.t3, styles.findDifferentStay)}
          onClick={openModal}
        >
          Find a different stay
        </button>
      </div>
    );
  };

  return (
    <section
      className={cx(styles.container, className)}
      aria-label="hotel reservation"
    >
      <h3 className={cx(typography.h7, styles.heading)}>Hotel Reservation</h3>
      {renderBody()}
      <HotelLinkModal
        isOpen={isModalOpen}
        onClose={closeModal}
        reservationId={reservationId}
        onLinked={() => {
          closeModal();
          onUpdate?.();
        }}
      />
    </section>
  );
};
